import { useEffect, useState } from "react";
import { ChatScreen } from "./components/ChatScreen";
import { RealtimeChatScreen } from "./components/RealtimeChatScreen";
import { RealtimePreflight } from "./components/RealtimePreflight";
import { resolveEngine, type UseSpeechEngine } from "./conversation/engine";
import { useLiveEngine } from "./hooks/useLiveEngine";
import { useRealtimeEngine } from "./hooks/useRealtimeEngine";
import { ErrorBanner } from "./components/ErrorBanner";
import { MetricsPanel } from "./components/MetricsPanel";
import { Preflight, type PreflightResult } from "./components/Preflight";
import { Completion } from "./components/Completion";
import { UnsupportedDevice } from "./components/UnsupportedDevice";
import { loadCompletion, clearCompletion } from "./state/completion";
import { checkMicSupport } from "./audio/micPermission";
import { detectVoiceSupport } from "./audio/voiceSupport";
import { useConnectivity } from "./state/connectivity";
import { identityProvider, sessionUploader } from "./storage/sessionUploaderInstance";
import { sessionEventBuffer } from "./storage/sessionEventBufferInstance";
import { useChatStore } from "./state/chatStore";
import { eventBus } from "./observability/eventBus";
import { attachEventDebugLogger } from "./observability/debugLogger";
import { attachCrashReporter } from "./observability/crashReporter";
import { flushPendingUploads } from "./storage/pendingUploads";

function shouldShowDebugPanel(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("debug") === "1";
}

// 開発・デモ用: ?preflight=skip で事前チェックを飛ばす (spec D8。被験者テストでは毎回実施)
function shouldSkipPreflight(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("preflight") === "skip";
}

// 開発・検証用: ?device=skip で端末判定を飛ばす (spec 2026-08-04 D8)。誤って非対応と
// 判定された人は一切回答できなくなるため、代償の大きさに見合う逃げ道として残す。
function shouldSkipDeviceCheck(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("device") === "skip";
}

type SessionBootstrap = { userId: string; sessionId: string; startedAt: string };

/**
 * 本編で読み込むアンケート。**id をここで持つのは、端末情報 (userAgent) を載せた
 * manifest を「アンケートを開いた時点」で書くため** — その時点ではまだ JSON を
 * ロードしておらず surveyId が分からない。本編に入ると ChatScreen が実際の id と
 * 氏名で上書きする (manifest の POST は全上書きなので冪等)。
 *
 * したがって id がずれても実害は「事前チェックだけで離脱した人の surveyId が古い」
 * だけで、回答を持つセッションは必ず正しい値に直る。
 */
const SURVEY = { id: "weekly-review", url: "/surveys/weekly-review.json" } as const;

export function App() {
  const [micSupported, setMicSupported] = useState(true);
  const [micMessage, setMicMessage] = useState("");
  const online = useConnectivity();
  const [offlineDismissed, setOfflineDismissed] = useState(false);
  const [showDebug] = useState(shouldShowDebugPanel);
  // 送信後にリロードされた場合の復帰先 (spec 2026-07-26 §3.2)。sessionStorage は同期に
  // 読めるので初期化時に判定でき、?preflight=skip より先に効く。
  const [completion] = useState(loadCompletion);
  // 音声で回答できない端末か (spec 2026-08-04)。判定は同期なので初期化子で決まり、
  // 事前チェックが一瞬見えてから案内に差し替わることがない。
  const [unsupported] = useState(() => !shouldSkipDeviceCheck() && !detectVoiceSupport().supported);
  const [screen, setScreen] = useState<"preflight" | "chat" | "done">(() =>
    completion ? "done" : shouldSkipPreflight() ? "chat" : "preflight"
  );
  const [session, setSession] = useState<SessionBootstrap | null>(null);
  const [preflightResult, setPreflightResult] = useState<PreflightResult | null>(null);

  useEffect(() => {
    if (!online) setOfflineDismissed(false);
  }, [online]);

  // barge-in 調査 (2026-07-25): ?event-debug=1 のときだけ eventBus の全イベントを console に
  // 出す。実機で「AI 発話中に話しても入力が反応しない」を切り分けるための診断モードで、
  // 既定 off (production は完全に無音)。unmount で購読解除する。
  useEffect(() => attachEventDebugLogger(eventBus), []);

  // 未捕捉例外 / unhandled rejection を app.crash として events.jsonl に残す
  // (spec 2026-07-29 §3.1.2)。debugLogger と違い既定で有効 — JS が落ちたときに
  // 痕跡がゼロになるのを防ぐのが目的なので、フラグで隠さない。
  useEffect(() => attachCrashReporter(), []);

  useEffect(() => {
    const s = checkMicSupport();
    setMicSupported(s.supported);
    if (!s.supported)
      setMicMessage(
        s.reason ?? "このブラウザは音声入力に対応していません。テキストでお答えください。"
      );
  }, []);

  // session bootstrap の前倒し: Preflight 中の preflight.* / mic.* イベントが
  // sessionId gate を通過し、離脱時も sendBeacon で events.jsonl に残るように、
  // sessionId 採番 + chatStore.startSession + sessionEventBuffer.attach を
  // ChatScreen mount 前に行う。manifest 作成 (sessionUploader.startSession) は
  // 従来どおり ChatScreen の survey ロード時 (同じ sessionId) に走る。
  // ChatScreen 側は clearChat() 後に同じ sessionId を startSession し直す (Task 11)。
  useEffect(() => {
    // 復元画面はアンケートを 1 問も進めないので session を採番しない。
    // 採番すると R2 に中身のない session が 1 つ立つ (spec 2026-07-26 §3.2)。
    // 「もう一度回答する」は記録を消してから reload するので、次の読み込みでは通常どおり走る。
    if (completion) return;
    // 非対応端末も 1 問も回答しないので採番しない (spec 2026-08-04 D6)。採番すると
    // R2 に中身のない session が 1 つ立つ。
    if (unsupported) return;
    let cancelled = false;
    void (async () => {
      let userId: string;
      try {
        userId = await identityProvider.getUserId();
      } catch {
        // identityProvider (localStorage 等) が失敗しても bootstrap は継続する:
        // 永続化されない一時 id でセッションを開始し、gate (session &&) が
        // 開かず画面が永久にブランクになる事態を防ぐ (原則 5、レビュー指摘の反映)。
        userId = crypto.randomUUID();
      }
      if (cancelled) return;
      const sessionId = crypto.randomUUID();
      const startedAt = new Date().toISOString();
      useChatStore.getState().startSession({ sessionId });
      sessionEventBuffer.attach({ userId, sessionId, startedAt });
      setSession({ userId, sessionId, startedAt });
      // 端末情報 (userAgent) を持つ manifest を**ここで**書く。本編突入まで待つと
      // 事前チェックだけで離脱した人の端末が 1 件も残らない (2026-08-05 の実データでは
      // manifest 22 件に対し events.jsonl は 47 件あり、差の 25 件がまさにこれ)。
      // await しない — R2 保存は fire-and-forget で UI を止めない (原則 5)。
      // 本編に入れば ChatScreen が同じ sessionId で氏名つきに上書きする。
      void sessionUploader.startSession({
        userId,
        sessionId,
        surveyId: SURVEY.id,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
        startedAt,
      });
      // 前回のセッションで送れなかった events を送り直す (spec 2026-07-29 §3.4.3)。
      // await しない — 前回の失敗が今回の起動を遅らせてはいけない (原則 5)。
      void flushPendingUploads();
    })();
    return () => {
      cancelled = true;
      // App unmount で snapshot interval / window listener / subscription を片付ける。
      // production では App は root なので通常 unmount しない (実離脱は pagehide →
      // stopSnapshotTimer が止める) が、enableSnapshots で attach() が real interval を
      // 張るようになったため、SPA 埋め込み / hot reload / テストの unmount で interval が
      // 孤児化しないよう defensive に detach する (detach は冪等)。
      sessionEventBuffer.detach();
    };
  }, [completion, unsupported]);

  // spec D10: Preflight は許可必須のため onReady 到達 = mic 許可済み。
  // micEnabled はブラウザ対応のみで決まる (?preflight=skip 経路も従来どおり)。
  const micEnabled = micSupported;

  // 音声で回答できない端末には案内だけを出して行き止まりにする (spec 2026-08-04 D1)。
  // 全画面で置き換えるのは、オフラインバナーやマイク非対応バナーが同時に出ると
  // 案内が 2 つ並んでどちらに従えばよいか分からなくなるため。
  // 完了記録があるときは完了画面を優先する — 回答を終えた人に「回答できません」と
  // 出しても、その人にできることは何も無い。
  if (unsupported && !completion) {
    return (
      <main className="h-[100dvh] flex flex-col bg-slate-50 overflow-hidden">
        <UnsupportedDevice />
      </main>
    );
  }

  // 会話エンジンの切り替え (spec 2026-08-29 D1 / 2026-09-14 L1)。speech-to-speech の 2 つ
  // (realtime = gpt-realtime-2.1 / live = GPT-Live-1) は画面を共有し、エンジンだけ差し替える (L2)。
  // 既定は cascade で、cascade 側の props は 1 つも変えていない。
  const engineKind = resolveEngine();
  const speechEngine: UseSpeechEngine | null =
    engineKind === "live" ? useLiveEngine : engineKind === "realtime" ? useRealtimeEngine : null;

  return (
    <main className="h-[100dvh] flex flex-col bg-slate-50 overflow-hidden">
      {!online && !offlineDismissed && (
        <ErrorBanner
          code="offline"
          message="オフラインです。回答は自動保存されているので、接続が戻り次第お試しください。"
          onDismiss={() => setOfflineDismissed(true)}
        />
      )}
      {!micSupported && (
        <ErrorBanner
          code="mic-not-supported"
          message={micMessage}
          onDismiss={() => setMicSupported(true)}
        />
      )}
      {screen === "done" && completion && (
        <Completion
          answered={completion.answered}
          total={completion.total}
          onRestart={() => {
            // 部分的な state リセットではなく reload にする (spec 2026-07-26 §5.3)。マイクは
            // 送信時に track ごと停止済みで許可フローの通し直しが要り、sessionId も
            // 採番し直す必要がある。
            clearCompletion();
            window.location.reload();
          }}
        />
      )}
      {session &&
        screen === "preflight" &&
        // 会話エンジンの切り替え (spec 2026-08-29 D1 / 段階 2)。既定は cascade。
        // step の構成と読み上げる文言はどのエンジンでも同じにしてあり、違うのは「誰が喋るか」と
        // 「氏名をどう取るか」だけ (比較の対象をエンジンの差に絞るため)。
        (speechEngine ? (
          <RealtimePreflight
            useEngine={speechEngine}
            micSupported={micSupported}
            userId={session.userId}
            onReady={(result) => {
              setPreflightResult(result);
              setScreen("chat");
            }}
          />
        ) : (
          <Preflight
            micSupported={micSupported}
            // 氏名音声の保存先 prefix に使う (spec 2026-07-29 §4.4)。bootstrap で 1 度だけ
            // 解決した値を渡す — Preflight 側で解決し直すと、localStorage が使えない環境で
            // events.jsonl と違う user prefix に散る。
            userId={session.userId}
            onReady={(result) => {
              setPreflightResult(result);
              setScreen("chat");
            }}
          />
        ))}
      {session &&
        screen === "chat" &&
        // 会話エンジンの切り替え (spec 2026-08-29 D1)。既定は cascade で、
        // ?engine=realtime / ?engine=live のときだけ speech-to-speech の経路を描く。
        // cascade 側の props は 1 つも変えていない (段階 1 は既定経路を触らない)。
        (speechEngine ? (
          <RealtimeChatScreen
            useEngine={speechEngine}
            surveyUrl={SURVEY.url}
            userId={session.userId}
          />
        ) : (
          <ChatScreen
            surveyUrl={SURVEY.url}
            micEnabled={micEnabled}
            session={session}
            displayName={preflightResult?.displayName}
            outputDeviceId={preflightResult?.outputDeviceId}
          />
        ))}
      {showDebug && <MetricsPanel />}
    </main>
  );
}
